import { useTranslation } from 'react-i18next';
import Card from '../ui/Card';
import StatusBadge from '../ui/StatusBadge';
import SparklineChart from '../charts/SparklineChart';
import { useSparklineData } from '../../hooks/useMeasurementsCombined';
import type { ProbeWithStats } from '../../hooks/useDashboardData';

interface DashboardProbeCardProps {
  probeData: ProbeWithStats;
  siteName: string;
  loggerName: string;
}

export default function DashboardProbeCard({
  probeData,
  siteName,
  loggerName,
}: DashboardProbeCardProps) {
  const { t } = useTranslation();
  const { probe, currentValue, min24h, max24h, status } = probeData;

  const { data: sparklineData = [], isLoading: sparklineLoading } =
    useSparklineData(probe.id);

  const amplitude =
    min24h !== undefined && max24h !== undefined
      ? max24h - min24h
      : undefined;

  return (
    <Card padding="sm" className="h-full">
      <div className="flex flex-col gap-2">
        {/* Header: probe name + status badge */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-medium text-gray-900 truncate flex-1">
            {probe.name}
          </h3>
          {status !== 'ok' && <StatusBadge status={status} />}
        </div>

        {/* Context: Site > Logger */}
        <p className="text-xs text-gray-500 truncate">
          {siteName} &gt; {loggerName}
        </p>

        {/* Current value */}
        {currentValue !== undefined ? (
          <div className="mt-1">
            <p className="text-xs text-gray-500">{t('probe.currentValue')}</p>
            <p className="text-2xl font-bold text-gray-900">
              {currentValue.toFixed(1)}
              <span className="text-sm font-normal text-gray-500 ml-1">{probe.unit}</span>
            </p>
          </div>
        ) : (
          <div className="mt-1">
            <p className="text-sm text-gray-400 italic">{t('probe.noData')}</p>
          </div>
        )}

        {/* Thresholds: alarm + warning compact */}
        {(probe.alarm_low != null || probe.alarm_high != null ||
          probe.warning_low != null || probe.warning_high != null) && (
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs">
            {probe.alarm_low != null && probe.alarm_high != null && (
              <span className="text-red-600">
                🚨 {Number(probe.alarm_low).toFixed(0)}–{Number(probe.alarm_high).toFixed(0)}{probe.unit}
              </span>
            )}
            {probe.warning_low != null && probe.warning_high != null && (
              <span className="text-yellow-600">
                ⚠ {Number(probe.warning_low).toFixed(0)}–{Number(probe.warning_high).toFixed(0)}{probe.unit}
              </span>
            )}
          </div>
        )}

        {/* Sparkline — last 1h of ADV data */}
        <div className="mt-1">
          {sparklineLoading ? (
            <div
              className="rounded animate-pulse bg-gray-100"
              style={{ height: 80 }}
            />
          ) : (
            <SparklineChart
              data={sparklineData}
              alarmLow={probe.alarm_low}
              alarmHigh={probe.alarm_high}
            />
          )}
        </div>

        {/* Min/Max 24h + Amplitude */}
        {min24h !== undefined && max24h !== undefined && (
          <div className="flex gap-3 mt-1 pt-2 border-t border-gray-100">
            <div className="flex-1">
              <p className="text-xs text-gray-500">{t('probe.min24h')}</p>
              <p className="text-sm font-semibold text-blue-600">
                {min24h.toFixed(1)}
                <span className="text-xs font-normal text-gray-500 ml-0.5">{probe.unit}</span>
              </p>
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500">{t('probe.max24h')}</p>
              <p className="text-sm font-semibold text-red-600">
                {max24h.toFixed(1)}
                <span className="text-xs font-normal text-gray-500 ml-0.5">{probe.unit}</span>
              </p>
            </div>
            {amplitude !== undefined && (
              <div className="flex-1">
                <p className="text-xs text-gray-500">{t('probe.amplitude')}</p>
                <p className="text-sm font-semibold text-gray-700">
                  {amplitude.toFixed(1)}
                  <span className="text-xs font-normal text-gray-500 ml-0.5">{probe.unit}</span>
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
